import classNames from 'classnames';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Card, Field, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import type { Template, TemplateInput } from './api-types';
import { VARIABLES } from './channels';
import MessagingNav from './messaging-nav';
import { ActionBar, ChannelPicker, NotFound, PreviewPanel, VariableBar, errorStatus, useVariableInsert } from './parts';

/** The editor for one template; keyed by id so switching templates starts fresh. */
const Editor = ({ stored }: { stored?: Template }) => {
  const navigate = useNavigate();
  const [t, setT] = useState<TemplateInput>(() =>
    stored ? { name: stored.name, subject: stored.subject, body: stored.body, channels: stored.channels } : { name: '', subject: '', body: '', channels: ['email'] },
  );
  const [notice, setNotice] = useState<'saved' | 'taken' | 'in-use' | 'failed' | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<TemplateInput>) => {
    setT((x) => ({ ...x, ...patch }));
    setNotice(null);
  };
  const { ref, insert } = useVariableInsert(t.body, (body) => set({ body }));

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { data } = stored ? await http.put<Template>(`/templates/${stored.id}`, t) : await http.post<Template>('/templates', t);
      if (stored) setNotice('saved');
      else navigate(`/messaging/templates/${data.id}`, { replace: true });
    } catch (err) {
      setNotice(errorStatus(err) === 409 ? 'taken' : 'failed');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!stored) return;
    setBusy(true);
    try {
      await http.delete(`/templates/${stored.id}`);
      navigate('/messaging/templates');
    } catch (err) {
      setNotice(errorStatus(err) === 409 ? 'in-use' : 'failed');
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Card aside={stored?.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}>
        <label className={field.label} htmlFor="template-name">
          Name
        </label>
        <input id="template-name" className={field.input} placeholder="Feature announcement" value={t.name} onChange={(e) => set({ name: e.target.value })} />

        <label className={`${field.label} mt-4`} htmlFor="template-subject">
          Email subject
        </label>
        <input id="template-subject" className={field.input} placeholder="New for {{casino_name}}" value={t.subject} onChange={(e) => set({ subject: e.target.value })} />
        <p className="mt-1 text-xs text-fg-muted">Telegram and the Owner Studio notification show it as the heading.</p>

        <label className={`${field.label} mt-4`} htmlFor="template-body">
          Text
        </label>
        {/* Owner Hub takes 4,000 characters, Telegram 4,096 (the backend checks the same). */}
        <textarea id="template-body" ref={ref} rows={10} maxLength={4000} className={field.textarea} value={t.body} onChange={(e) => set({ body: e.target.value })} />
        <VariableBar onInsert={insert} />

        <p className={`${field.label} mt-5`}>Meant for</p>
        <ChannelPicker value={t.channels} onChange={(channels) => set({ channels })} withNotes={false} />

        <ActionBar className="flex flex-wrap items-center gap-2 md:justify-end md:gap-3">
          {notice === 'saved' && (
            <span role="status" className="basis-full text-sm text-fg-muted md:basis-auto">
              Saved
            </span>
          )}
          {notice === 'taken' && <span className="basis-full text-sm text-danger md:basis-auto">Another template has this name.</span>}
          {notice === 'in-use' && <span className="basis-full text-sm text-danger md:basis-auto">A trigger still uses it: change the trigger first.</span>}
          {notice === 'failed' && <span className="basis-full text-sm text-danger md:basis-auto">That did not work. Try again.</span>}
          {stored && (
            <button type="button" className={classNames(btn.danger, 'flex-1 md:flex-none')} disabled={busy} onClick={remove}>
              Delete
            </button>
          )}
          <button type="button" className={classNames(btn.primary, 'flex-1 md:flex-none')} disabled={!t.name.trim() || !t.body.trim() || busy} onClick={save}>
            Save template
          </button>
        </ActionBar>
      </Card>

      <div className="space-y-4 md:space-y-6">
        <Card title="Preview" aside={<span className="text-xs text-fg-faint">With sample values</span>}>
          <PreviewPanel channels={t.channels} title={t.subject || t.name} body={t.body} />
        </Card>
        <Card title="Variables">
          <dl>
            {VARIABLES.map((v) => (
              <Field key={v.key} label={v.label}>
                <span className="font-mono text-xs text-fg">{`{{${v.key}}}`}</span>
                <span className="block break-all text-xs text-fg-muted">For example {v.sample}</span>
              </Field>
            ))}
          </dl>
          {stored && <p className="mt-3 text-xs text-fg-muted">Used by {stored.used_by} triggers and messages.</p>}
        </Card>
      </div>
    </div>
  );
};

/** One template (OUT-1, OUT-2): name, subject, text with variables, and a preview per channel. */
const TemplateEditorPage = () => {
  const { id } = useParams();
  const { data, error } = useApi<Template>(id ? `/templates/${encodeURIComponent(id)}` : null);

  let body;
  if (error === 'Not found.') body = <NotFound what="template" back="/messaging/templates" label="Back to templates" />;
  else if (error && !data) body = <StateMessage title="The template could not be loaded" tone="error">{error}</StateMessage>;
  else if (id && !data) body = <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />;
  else body = <Editor key={id ?? 'new'} stored={data ?? undefined} />;

  return (
    <div>
      <PageHeader
        title={id ? (data?.name ?? 'Template') : 'New template'}
        description="A text with variables, shown the same way on every channel it is meant for."
        back={{ to: '/messaging/templates', label: 'Templates' }}
      >
        <MessagingNav />
      </PageHeader>
      {body}
    </div>
  );
};

export default TemplateEditorPage;
